import React, { useState } from 'react';
import {
  Dialog,
  LoadingIndicator,
  Button,
  Text,
} from '@uhg-harmony/uitk-react';
import styled from 'styled-components';
import { searchWidgetsByType } from 'shared/widgets';
import {
  updateFormWidget as doUpdateFormWidget,
  createFormWidget as doCreateFormWidget,
} from 'shared/api/sites';
import { SiteSlice } from 'shared/state/ducks/site';
import { filterValidPages } from 'shared/util/pageUtils';

const StyledButton = styled(Button)`
  font-family: OptumSans, sans-serif;
`;

const StyledDialog = styled(Dialog)`
  z-index: 999;
  font-family: OptumSans, sans-serif;
  & > div > div > div + h1 {
    font-family: OptumSans, sans-serif;
  }
  & > div > div > div > button {
    font-family: OptumSans, sans-serif;
  }
`;

export const StyledTextComponent = styled(Text)`
  font-family: OptumSans, sans-serif;
`;

interface UserSyncChangesDialogProps {
  hasChanged: boolean;
  pages: Array<Page>;
  saveSite: (showToastMessage?: boolean) => Promise<Site>;
  saveSitePages?: (site: Site, pages: Array<Page>) => void;
  resetUserSyncChange?: () => void;
  site: Site;
  savePage: (
    pageId: number,
    page: PageVersion,
    skipSiteRefresh?: boolean
  ) => Promise<void>;
  refreshSiteBaseline?: () => void;
  page: Page;
  swapResetData?: () => void;
  userModifiedDataState?: SiteSlice;
  updateSite: (site: Site) => void;
  updateCurrentPage: (pageVersion: PageVersion) => void;
  currentPage: PageVersion;
  showToastMessage: boolean;
}

export const UserSyncChangesDialog: React.FC<UserSyncChangesDialogProps> = ({
  hasChanged,
  pages,
  saveSite,
  saveSitePages,
  resetUserSyncChange,
  site,
  savePage,
  refreshSiteBaseline,
  page,
  swapResetData,
  userModifiedDataState,
  updateSite,
  updateCurrentPage,
  currentPage,
  showToastMessage,
}) => {
  const [loading, setLoading] = useState(false);

  const userSyncDismissChange = () => {
    swapResetData();
    const modifiedSiteMetaData = { ...userModifiedDataState };
    delete modifiedSiteMetaData.pages;
    updateSite(modifiedSiteMetaData);
    const serverPage = userModifiedDataState.pages?.find(
      page => page.id == currentPage.pageId
    );
    // Only update editor content if the server page has full content.
    // GET /pages (list) no longer returns content, so serverPage.current.content
    // may be undefined — updating with it would blank the editor.
    if (serverPage?.current?.content) {
      updateCurrentPage(serverPage.current);
    }
  };

  const userSyncSaveChange = () => {
    setLoading(true);
    const filteredPages = filterValidPages(pages);
    saveSite(showToastMessage)
      .then(response => {
        saveSitePages(response, pages);
        resetUserSyncChange();
        // Save all visited pages in parallel with skipSiteRefresh=true so
        // each page save does NOT trigger its own GET /current call.
        // Instead, call refreshSiteBaseline once after all saves complete.
        Promise.all(
          filteredPages.map(page => handleSavePage(page.id, page.current, true))
        ).then(() => {
          refreshSiteBaseline?.();
          setLoading(false);
        });
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const userCloseDialogBox = () => {
    resetUserSyncChange();
  };

  const handleSavePage = (
    pageId: number,
    pageVersion: PageVersion,
    skipSiteRefresh?: boolean
  ): Promise<void> => {
    const { forms } = pageVersion;
    forms?.create?.forEach(item => {
      handleFormWidget('create', item);
    });
    forms?.update?.forEach(item => {
      handleFormWidget('update', item);
    });

    return savePage(pageId, pageVersion, skipSiteRefresh);
  };

  const handleFormWidget = (
    action: string,
    widgetId: String
  ): Promise<any> | void => {
    const widget = searchWidgetsByType(
      page.current.content,
      'root',
      new Array<Widget<any>>(),
      'Form'
    ).find(widget => widget.id === widgetId);
    if (widget?.config.formActionType === 'email') {
      const conditionalEmailList = [
        ...(widget.config.conditionalEmailConfig || []),
      ].map(conditionalEmail => ({
        ...conditionalEmail,
        email: [conditionalEmail.email],
      }));
      switch (action) {
        case 'create':
          return doCreateFormWidget(site.siteId, page.id, {
            widgetId: widget.id,
            recipientEmails: [widget.config.emailTo],
            enableConditionalEmailTo: widget.config.conditionalEmailEnabled,
            conditionalEmailList: conditionalEmailList,
          });
        case 'update':
          return doUpdateFormWidget(site.siteId, page.id, {
            widgetId: widget.id,
            recipientEmails: [widget.config.emailTo],
            enableConditionalEmailTo: widget.config.conditionalEmailEnabled,
            conditionalEmailList: conditionalEmailList,
          });
        case 'delete':
          break;
        default:
          break;
      }
    }
  };

  if (!hasChanged) return null;

  return (
    <StyledDialog
      title="Save Changes"
      titleAs="h1"
      onClose={userCloseDialogBox}
    >
      <Dialog.Body>
        <StyledTextComponent>
          Someone else modified this site while you were editing it.
        </StyledTextComponent>
        {loading && <LoadingIndicator size="s" loading={loading} />}
      </Dialog.Body>
      <Dialog.Actions>
        <StyledButton
          data-test-id="user-sync-save-changes"
          onPress={userSyncSaveChange}
          disabled={loading}
        >
          Save your changes
        </StyledButton>
        <StyledButton
          data-test-id="user-sync-discard-changes"
          onPress={userSyncDismissChange}
          variant="tertiary"
        >
          Discard changes and show new version
        </StyledButton>
      </Dialog.Actions>
    </StyledDialog>
  );
};
